import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input } from "./Input";
import type { IInputNumberProps } from "./types";
import { detectNumberFormat } from "../../utils";

export const InputNumber = (props: IInputNumberProps) => {
  const detectedFormat = useMemo(() => detectNumberFormat(), []);

  const {
    onChange,
    decimalPlaces,
    decimalSeparator = detectedFormat.decimalSeparator,
    thousandsSeparator = detectedFormat.thousandsSeparator,
    allowChars,
    value,
    maxLength,
    ...IInputProps
  } = props;

  const [displayValue, setDisplayValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number>(0);
  const cursorPositionShiftRef = useRef<number>(0);
  const prevValueRef = useRef<string>("");

  // Memoize regex patterns
  const cleanPattern = useRef<RegExp | undefined>(undefined);
  const decimalPattern = useRef<RegExp | undefined>(undefined);
  const leadingZerosPattern = useMemo(() => /^-?0+\d/, []);
  const removeLeadingZerosPattern = useMemo(() => /^-?0+/, []);

  useEffect(() => {
    cleanPattern.current = new RegExp(
      `[^0-9${decimalSeparator}${allowChars || ""}]`,
      "g",
    );
    decimalPattern.current = new RegExp(
      decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "g",
    );
  }, [decimalSeparator, allowChars]);

  const formatWithThousands = useCallback(
    (value: string): string => {
      if (!thousandsSeparator) return value;

      const parts = value.split(decimalSeparator);
      const integerPart = parts[0];
      const decimalPart = parts[1];

      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        thousandsSeparator,
      );

      return decimalPart !== undefined
        ? `${formattedInteger}${decimalSeparator}${decimalPart}`
        : formattedInteger;
    },
    [thousandsSeparator, decimalSeparator],
  );

  const countSeparators = useCallback(
    (str: string): number => {
      if (!thousandsSeparator) return 0;
      const escapedSeparator = thousandsSeparator.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      return (str.match(new RegExp(escapedSeparator, "g")) || []).length;
    },
    [thousandsSeparator],
  );

  const convertToUSFormat = useCallback(
    (value: string): string => {
      if (!value) return value;
      if (decimalSeparator !== ".") {
        return value.replace(decimalSeparator, ".");
      }
      return value;
    },
    [decimalSeparator],
  );

  const convertFromUSFormat = useCallback(
    (value: string): string => {
      if (!value) return value;
      if (decimalSeparator !== ".") {
        return value.replace(".", decimalSeparator);
      }
      return value;
    },
    [decimalSeparator],
  );

  const handleChange = useCallback(
    (value: string) => {
      const prevValue = prevValueRef.current;

      const isDelimiter = value.at(-1) === decimalSeparator;
      if (
        isDelimiter &&
        (!decimalPlaces ||
          value.length === 1 ||
          (value.length === 2 && value.startsWith("-")))
      ) {
        return;
      }

      const delimitersCount = (value.match(decimalPattern.current!) || [])
        .length;
      if (delimitersCount > 1) {
        return;
      }

      const allowMinus =
        allowChars === undefined || (allowChars?.includes("-") ?? false);
      const hasMinus = allowMinus && value.startsWith("-");
      const valueWithoutMinus = hasMinus ? value.substring(1) : value;

      let cleanValue = valueWithoutMinus.replace(cleanPattern.current!, "");

      if (hasMinus) {
        cleanValue = "-" + cleanValue;
      }

      const tmp = cleanValue.split(decimalSeparator);
      let int = tmp[0];
      const decimal = tmp[1];
      if (decimal && decimalPlaces) {
        cleanValue = `${int}${decimalSeparator}${decimal.substring(0, decimalPlaces)}`;
      }

      if (leadingZerosPattern.test(int)) {
        const isNegative = int.startsWith("-");
        const digits = int.replace(removeLeadingZerosPattern, "");
        int = (isNegative ? "-" : "") + (digits || "0");
        cleanValue =
          decimal !== undefined ? `${int}${decimalSeparator}${decimal}` : int;
      }

      const formattedValue = formatWithThousands(cleanValue);

      const currentCursor =
        (inputRef.current?.selectionStart || 0) +
        cursorPositionShiftRef.current;

      if (cursorPositionShiftRef.current !== 0) {
        cursorPositionShiftRef.current = 0;
      }

      const valueUnchanged = formattedValue === prevValue;

      const isDeleting = value.length < prevValue.length;
      const isDeletingForward =
        isDeleting &&
        (inputRef.current?.selectionStart || 0) === cursorPositionRef.current;

      // Check maxLength against clean numeric value (no separators)
      if (maxLength && cleanValue.length > maxLength && !isDeleting) {
        return;
      }

      let newCursorPos = currentCursor;

      if (valueUnchanged && !isDeleting) {
        newCursorPos = cursorPositionRef.current;
      } else if (!isDeleting) {
        const separatorsBefore = countSeparators(prevValue);
        const separatorsAfter = countSeparators(formattedValue);
        const separatorsDiff = separatorsAfter - separatorsBefore;

        newCursorPos = currentCursor + separatorsDiff;
      } else {
        const justDeletedSeparatorBackward =
          !isDeletingForward &&
          currentCursor < prevValue.length &&
          prevValue[currentCursor] === thousandsSeparator &&
          thousandsSeparator;

        const justDeletedSeparatorForward =
          isDeletingForward &&
          currentCursor < prevValue.length &&
          prevValue[currentCursor] === thousandsSeparator &&
          thousandsSeparator;

        if (justDeletedSeparatorBackward) {
          const beforeDigit = prevValue.substring(0, currentCursor - 1);
          const afterSep = prevValue.substring(currentCursor + 1);
          const newValue = beforeDigit + afterSep;

          prevValueRef.current = newValue;
          cursorPositionShiftRef.current = -1;

          return handleChange(newValue);
        }

        if (justDeletedSeparatorForward) {
          const beforeSep = prevValue.substring(0, currentCursor);
          const afterDigit = prevValue.substring(currentCursor + 2);
          const newValue = beforeSep + afterDigit;

          prevValueRef.current = newValue;
          cursorPositionShiftRef.current = 0;

          return handleChange(newValue);
        }

        const separatorsBefore = countSeparators(
          prevValue.substring(0, currentCursor),
        );
        const separatorsAfter = countSeparators(
          formattedValue.substring(0, currentCursor),
        );
        const separatorsDiff = separatorsAfter - separatorsBefore;

        newCursorPos = Math.max(0, currentCursor + separatorsDiff);
      }

      setDisplayValue(formattedValue);
      prevValueRef.current = formattedValue;
      cursorPositionRef.current = newCursorPos;

      if (onChange) {
        const usFormatValue = convertToUSFormat(cleanValue);
        onChange(usFormatValue);
      }
    },
    [
      onChange,
      decimalPlaces,
      decimalSeparator,
      allowChars,
      formatWithThousands,
      thousandsSeparator,
      maxLength,
      countSeparators,
      convertToUSFormat,
    ],
  );

  const updateCursorPosition = useCallback(() => {
    if (inputRef.current) {
      cursorPositionRef.current = inputRef.current.selectionStart || 0;
    }
  }, []);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      updateCursorPosition();
      IInputProps.onKeyUp?.(e);
    },
    [IInputProps.onKeyUp, updateCursorPosition],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      updateCursorPosition();
      IInputProps.onClick?.(e);
    },
    [IInputProps.onClick, updateCursorPosition],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (decimalPlaces && displayValue.endsWith(decimalSeparator)) {
        const newValue = displayValue + "0";
        setDisplayValue(newValue);

        let cleanValue = newValue;
        if (thousandsSeparator) {
          const escapedSeparator = thousandsSeparator.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
          );
          cleanValue = cleanValue.replace(
            new RegExp(escapedSeparator, "g"),
            "",
          );
        }
        const usFormatValue = convertToUSFormat(cleanValue);
        onChange?.(usFormatValue);
      }

      IInputProps.onBlur?.(e);
    },
    [
      displayValue,
      decimalSeparator,
      decimalPlaces,
      thousandsSeparator,
      convertToUSFormat,
      onChange,
      IInputProps,
    ],
  );

  useEffect(() => {
    if (value !== undefined) {
      const stringValue = String(value);
      if (stringValue) {
        const displayFormatValue = convertFromUSFormat(stringValue);
        const formattedValue = formatWithThousands(displayFormatValue);
        setDisplayValue(formattedValue);
      } else {
        setDisplayValue("");
      }
    }
  }, [value, formatWithThousands, convertFromUSFormat]);

  useLayoutEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== undefined) {
      const position = cursorPositionRef.current;
      inputRef.current.setSelectionRange(position, position);
    }
  });

  if (prevValueRef.current !== displayValue) {
    prevValueRef.current = displayValue;
  }

  const inputModeValue = useMemo(
    () => (decimalPlaces ? "decimal" : "numeric"),
    [decimalPlaces],
  );

  const autoCompleteValue = useMemo(
    () => IInputProps.autoComplete || "off",
    [IInputProps.autoComplete],
  );

  return (
    <Input
      {...IInputProps}
      ref={inputRef}
      value={displayValue}
      autoComplete={autoCompleteValue}
      onChange={handleChange}
      onKeyUp={handleKeyUp}
      onClick={handleClick}
      onBlur={handleBlur}
      inputMode={inputModeValue}
    />
  );
};
